import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { ActiveMeasuringPointsState, EditDialogMode } from "./activeMeasuringPointsTypes";
import { ActiveMeasuringPointDto } from "../../app/apislices/activeMeasuringPointApi";
import { exportSections } from "./exportConfig";
import * as XLSX from "xlsx";

const initialState: ActiveMeasuringPointsState = {
  selectedActiveMeasuringPoint: undefined,
  isSelectionDialogOpen: false,
  editDialogMode: "hidden",
  isExportDialogOpen: false,
  exportColumns: [],
};

const activeMeasuringPointsSlice = createSlice({
  name: "activeMeasuringPointsSlice",
  initialState,
  reducers: {
    setSelectedActiveMeasuringPoint: (state, action: PayloadAction<ActiveMeasuringPointDto | undefined>) => {
      state.selectedActiveMeasuringPoint = action.payload;
    },
    setIsSelectionDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSelectionDialogOpen = action.payload;
    },
    setEditDialogMode: (state, action: PayloadAction<EditDialogMode>) => {
      state.editDialogMode = action.payload;
    },
    setIsExportDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isExportDialogOpen = action.payload;
    },
    toggleColumnForExport: (state, action: PayloadAction<string>) => {
      if (state.exportColumns.includes(action.payload)) {
        state.exportColumns = state.exportColumns.filter((x) => x !== action.payload);
      } else {
        state.exportColumns.push(action.payload);
      }
    },
    toggleColumnsOfCategoryForExport: (state, action: PayloadAction<string>) => {
      const columnsOfCategory = exportSections
        .filter((x) => x.category === action.payload)
        .flatMap((x) => x.exportColumns.map((y) => y.name));
      if (columnsOfCategory.every((x) => state.exportColumns.includes(x)))
        state.exportColumns = state.exportColumns.filter((x) => !columnsOfCategory.includes(x));
      else state.exportColumns = state.exportColumns.filter((x) => !columnsOfCategory.includes(x)).concat(columnsOfCategory);
    },
    addColumnsOfCategoryToSelectionForExport: (state, action: PayloadAction<string>) => {
      const columnsOfCategory = exportSections
        .filter((x) => x.category === action.payload)
        .flatMap((x) => x.exportColumns.map((y) => y.name));
      state.exportColumns = state.exportColumns.filter((x) => !columnsOfCategory.includes(x)).concat(columnsOfCategory);
    },
    removeColumnsOfCategoryToSelectionForExport: (state, action: PayloadAction<string>) => {
      const columnsOfCategory = exportSections
        .filter((x) => x.category === action.payload)
        .flatMap((x) => x.exportColumns.map((y) => y.name));
      state.exportColumns = state.exportColumns.filter((x) => !columnsOfCategory.includes(x));
    },
  },
});

export const selectPercentageOfSelectedColumnsOfCategory = createSelector(
  [(state) => state.activeMeasuringPoints.exportColumns, (state, category) => category],
  (exportColumns, category) => {
    const columnsOfCategory = exportSections
      .filter((x) => x.category === category)
      .flatMap((x) => x.exportColumns.map((y) => y.name));
    return columnsOfCategory.filter((x) => exportColumns.includes(x)).length / columnsOfCategory.length;
  }
);

export const {
  setSelectedActiveMeasuringPoint,
  setIsSelectionDialogOpen,
  setEditDialogMode,
  setIsExportDialogOpen,
  toggleColumnForExport,
  toggleColumnsOfCategoryForExport,
  addColumnsOfCategoryToSelectionForExport,
  removeColumnsOfCategoryToSelectionForExport,
} = activeMeasuringPointsSlice.actions;

export default activeMeasuringPointsSlice.reducer;

export const exportToExcel = (data: any[], selection: Array<string>): void => {
  const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  let ws = XLSX.utils.aoa_to_sheet([]);
  ws = XLSX.utils.sheet_add_json(ws, data, { origin: "A1" });
  ws = XLSX.utils.sheet_add_aoa(ws, [
    exportSections.flatMap((e) => e.exportColumns.filter((x) => selection.includes(x.name)).map((y) => e.label + " " + y.label)),
  ]);
  ws["!cols"] = fitToColumn(data);

  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const dataOut = new Blob([excelBuffer], { type: fileType });
  downloadFile(dataOut, "AktiveMesspunkte" + fileExtension);
};

const fitToColumn = (data: any[], headerNames: string[] = []) => {
  const columnWidths: Array<{ wch: number }> = [];
  let index = 0;
  for (const property in data[0]) {
    columnWidths.push({
      wch: Math.max(
        headerNames.length == Object.keys(data[0]).length
          ? //eslint-disable-next-line security/detect-object-injection
            headerNames[index].length
          : property
            ? property.toString().length
            : 0,
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...data.map((obj: any) =>
          //eslint-disable-next-line @typescript-eslint/no-explicit-any
          obj[property as keyof any] ? obj[property as keyof any].toString().length : 0
        )
      ),
    });
    index++;
  }
  return columnWidths;
};

const downloadFile = (result: Blob, filename: string) => {
  const hiddenElement = document.createElement("a");
  const url = window.URL || window.webkitURL;
  const blobPDF = url.createObjectURL(result);
  hiddenElement.href = blobPDF;
  hiddenElement.target = "_blank";
  hiddenElement.download = filename;
  hiddenElement.click();
  hiddenElement.remove();
  URL.revokeObjectURL(blobPDF);
  return { data: null };
};
